import { HangingGameSignArtwork, HangingGameSignDetails } from './full/HangingGameSign';
import { getDrawStatusKey } from './drawStatus';
import './CardRecover.css';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  isPlaceholderOrganizerUsername,
  type BoxInfo,
  type CardAccessRecoverCard,
  type CardAccessRecoverMethod,
} from '@lub0v/santa-utils';
import {
  Button,
  Card,
  Container,
  HintError,
  Input,
  OtpChallenge,
  ParticipantFace,
  ParticipantTile,
  SantaAvatar,
  Spinner,
  Stack,
  Typography,
} from '@src/components';
import { verifyOtpCode } from '@src/pages/auth/auth';
import { apiUrlPath } from '@src/routing/urls';
import { santaClient } from '@src/state/client';

export interface CardRecoverProps {
  boxKey: string;
  cardKey: string;
  /** A spent token still names the card it was for, which the backend needs. */
  token?: string;
  /** Show recovery methods inside the public box page without exposing a card URL. */
  embedded?: boolean;
}

/**
 * A named card this browser cannot open: the personal link has been used, or
 * the person is on a different device. The backend says which ways back in
 * exist for that card, and each one is offered as a numbered step, as in v1.
 */
export function CardRecover({ boxKey, cardKey, token = '', embedded = false }: CardRecoverProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState<CardAccessRecoverCard | null>(null);
  const [boxName, setBoxName] = useState('');
  const [gameInfo, setGameInfo] = useState<BoxInfo | null>(null);
  const [methods, setMethods] = useState<CardAccessRecoverMethod[]>([]);
  const [emailMasked, setEmailMasked] = useState('');

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [challengeToken, setChallengeToken] = useState('');

  const [code, setCode] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      santaClient.getCardAccessRecoverMethods({ key: boxKey, cardKey, token }),
      santaClient.getBoxRecoverCards({ key: boxKey }),
    ])
      .then(([methodsResult, boxResult]) => {
        if (cancelled) {
          return;
        }
        setCard(methodsResult.data?.card ?? null);
        setGameInfo(boxResult.data?.gameInfo ?? null);
        setBoxName(methodsResult.data?.boxName ?? boxResult.data?.name ?? '');
        setEmailMasked(methodsResult.data?.emailMasked ?? '');
        setMethods(methodsResult.data?.card ? (methodsResult.data.methods ?? []) : []);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [boxKey, cardKey, token]);

  const isOrganizer = embedded || Boolean(card?.isAdmin || isPlaceholderOrganizerUsername(card?.username));

  const showParticipant = card?.isParticipant ?? !isOrganizer;

  /* Social recovery needs an account behind the card, which a quick draw does
   * not have, and "previous device" is advice rather than a step to take. */
  const shown = useMemo(
    () => methods.filter((method) => method.type !== 'social' && !(isOrganizer && method.type === 'organizer')),
    [methods, isOrganizer],
  );

  async function sendEmailLink() {
    setSending(true);
    setSendError(null);
    const result = await santaClient.recoverCardAccess({ key: boxKey, cardKey, token });
    setSending(false);
    if (result.ok && result.data?.challengeToken) {
      setChallengeToken(result.data.challengeToken);
      if (result.data.emailMasked) {
        setEmailMasked(result.data.emailMasked);
      }
      return;
    }
    setSendError(
      result.error?.status === 429 || result.error?.message === 'Too many requests'
        ? 'box.cardRecover.emailRateLimited'
        : (result.error?.message ?? 'CARD_ACCESS_UNAVAILABLE'),
    );
  }

  async function submitCode() {
    const value = code.trim();
    if (!value) {
      return;
    }
    setCodeBusy(true);
    setCodeError(null);
    const result = await santaClient.consumeCardRecoveryCode({ key: boxKey, cardKey, code: value });
    if (result.ok && result.data?.redirect) {
      // Follow the recovered role’s destination and reload session state. Keep
      // the current frontend origin when the API is configured for another host.
      window.location.replace(apiUrlPath(result.data.redirect));
      return;
    }
    setCodeBusy(false);
    setCodeError(
      result.error?.status === 429 || result.error?.message === 'Too many requests'
        ? 'box.cardRecover.codeRateLimited'
        : 'box.cardRecover.codeWrong',
    );
  }

  function hero(title: string, lead: string, heroTestId: string) {
    return <PageHeading title={title} subtitle={lead} testId={heroTestId} />;
  }

  if (loading) {
    return (
      <Stack gap={4} align="center" testId="box-card-recover-loading">
        <Spinner size="medium" label={t('box.loading')} />
      </Stack>
    );
  }

  if (!card) {
    return (
      <Container width="measure">
        <Stack gap={8} wide align="center" testId="box-card-recover-missing">
          <PageHeading
            title={<>{t('box.cardRecover.notFoundTitle')}</>}
            subtitle={<>{t('box.cardRecover.notFoundHint')}</>}
          />
        </Stack>
      </Container>
    );
  }

  if (challengeToken) {
    return (
      <Container width="measure">
        <Stack gap={8} wide align="center" testId="box-card-recover-otp">
          {hero(
            t('box.cardRecover.emailTitle'),
            t('box.cardRecover.emailHintPlain'),
            'box-card-recover-hero',
          )}

          <Card surface="plain" edges={{ pieces: 3 }} slot testId="box-card-recover-otp-panel">
            <OtpChallenge
              challengeToken={challengeToken}
              onVerify={(token, code) => verifyOtpCode(navigate, token, code)}
              testId="box-card-recover-code"
              hint={
                <Trans
                  i18nKey="box.cardRecover.emailSentHint"
                  values={{ email: emailMasked }}
                  components={{ email: <Typography as="span" role="body/strong" /> }}
                />
              }
            />
          </Card>

          <Button
            variant="ghost"
            onClick={() => setChallengeToken('')}
            testId="box-card-recover-back"
          >
            {t('box.cardRecover.backToMethods')}
          </Button>
        </Stack>
      </Container>
    );
  }

  function methodCard(method: CardAccessRecoverMethod, step: number) {
    if (method.type === 'email') {
      return (
        <Card
          key={method.type}
          surface="plain"
          tone="info"
          step={step}
          title={t('box.cardRecover.emailTitle')}
          testId="box-card-recover-email"
        >
          <Stack gap={4}>
            <Typography as="p" role="body/sm">
              {emailMasked
                ? t('box.cardRecover.emailHint', { email: emailMasked })
                : t('box.cardRecover.emailHintPlain')}
            </Typography>
            <HintError error={sendError} testId="box-card-recover-email-error" />
            <Button
              variant="primary"
              loading={sending}
              onClick={() => void sendEmailLink()}
              testId="box-card-recover-email-send"
            >
              {t('box.cardRecover.emailButton')}
            </Button>
          </Stack>
        </Card>
      );
    }

    if (method.type === 'recovery_codes') {
      return (
        <Card
          key={method.type}
          surface="plain"
          tone="info"
          step={step}
          title={t('box.cardRecover.codesTitle')}
          testId="box-card-recover-codes"
        >
          <Stack gap={4}>
            <Typography as="p" role="body/sm">
              {t(isOrganizer ? 'box.cardRecover.codesHintOrganizer' : 'box.cardRecover.codesHint')}
            </Typography>
            <Input
              value={code}
              onValueChange={(value) => {
                setCode(value.toUpperCase());
                setCodeError(null);
              }}
              label={t('box.cardRecover.codeLabel')}
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
              autoComplete="one-time-code"
              onEnter={() => void submitCode()}
              testId="box-card-recover-code-input"
            />
            <HintError error={codeError} testId="box-card-recover-code-error" />
            <Button
              variant="secondary"
              loading={codeBusy}
              disabled={!code.trim()}
              onClick={() => void submitCode()}
              testId="box-card-recover-code-submit"
            >
              {t('box.cardRecover.codeButton')}
            </Button>
          </Stack>
        </Card>
      );
    }

    if (method.type === 'previous_device') {
      return (
        <Card
          key={method.type}
          surface="plain"
          tone="neutral"
          step={step}
          title={t(
            isOrganizer ? 'box.cardRecover.deviceTitleOrganizer' : 'box.cardRecover.deviceTitle',
          )}
          testId="box-card-recover-device"
        >
          {t(isOrganizer ? 'box.cardRecover.deviceHintOrganizer' : 'box.cardRecover.deviceHint')}
        </Card>
      );
    }

    return (
      <Card
        key={method.type}
        surface="plain"
        tone="neutral"
        step={step}
        title={t('box.cardRecover.organizerTitle')}
        testId="box-card-recover-organizer"
      >
        {t(
          isOrganizer ? 'box.cardRecover.organizerHintOrganizer' : 'box.cardRecover.organizerHint',
        )}
      </Card>
    );
  }

  return (
    <Container width="measure">
      <Stack gap={8} wide align="center" testId="box-card-recover">
        {!embedded && (gameInfo ? (
          <div className="card-recover__game">
            <div className="card-recover__sign" data-testid="box-card-recover-identity">
              <HangingGameSignArtwork
                name={gameInfo.box.name}
                picture={gameInfo.box.picture}
                logo={gameInfo.box.logo}
              >
                <HangingGameSignDetails
                  organizer={undefined}
                  drawn={gameInfo.isActiveDraw && !gameInfo.box.isArchived}
                  status={t(getDrawStatusKey(gameInfo))}
                />
              </HangingGameSignArtwork>
            </div>
          </div>
        ) : null)}
        {!embedded && hero(
          t('box.cardRecover.titleOrganizer'),
          t('box.cardRecover.hintOrganizer'),
          'box-card-recover-hero',
        )}

        {!embedded && (gameInfo ? (
          showParticipant ? (
            <div className="card-recover__game">
              <ParticipantTile
                listLayout
                avatar={
                  <ParticipantFace card={card} size="identity" />
                }
                name={card.username || t('box.recover.privateParticipant')}
                testId="box-card-recover-participant"
              />
            </div>
          ) : null
        ) : (
          <ParticipantTile
            standalone
            avatar={
              isOrganizer ? (
                <SantaAvatar size="identity" />
              ) : (
                <ParticipantFace card={card} size="identity" />
              )
            }
            name={isOrganizer ? boxName : card.username || t('box.recover.privateParticipant')}
            testId="box-card-recover-identity"
          />
        ))}

        <Stack gap={4} wide>
          {shown.map((method, index) => methodCard(method, index + 1))}
        </Stack>
      </Stack>
    </Container>
  );
}
